import _ from 'lodash';
import {
  HandlingRequestStatus,
  HandlingRequestType,
  PackagingOptions,
  StockLotCRUDSchema,
  StockLotQuerySchemaLite,
} from 'types/inventory';
import { HandlingRequestSchema, TAPE_PACKAGES } from 'types/clientV2/stock';
import { WarehouseHandlingRequest } from 'views/warehouse/Kitting/HandlingRequestsContext';

export type ExpandedHandlingRequestWithGroupedStockLots =
  WarehouseHandlingRequest & {
    stockLots: StockLotQuerySchemaLite[];
    handlingRequests: WarehouseHandlingRequest[];
    ids: HandlingRequestSchema['id'][];
  };

export const groupHandlingRequests = (
  requests: WarehouseHandlingRequest[]
): WarehouseHandlingRequest[][] => {
  const groups: Record<string, WarehouseHandlingRequest[]> = {};

  requests.forEach((request) => {
    const groupId = request.splitStockLot ?? '';

    if (groups[groupId]) {
      groups[groupId].push(request);
    } else {
      groups[groupId] = [request];
    }
  });

  return Object.values(groups);
};

export const convertGroupedHandlingRequestsToObjects = (
  groupedHandlingRequests: WarehouseHandlingRequest[][]
): ExpandedHandlingRequestWithGroupedStockLots[] => {
  const groupedHandlingRequestObjects = groupedHandlingRequests.map((group) => {
    const stockLots = _.uniqBy(
      group.map((request) => request.stockLot),
      'id'
    );
    return {
      ...group[0],
      stockLots,
      handlingRequests: group,
      ids: group.map((request) => request.id),
    };
  });
  return groupedHandlingRequestObjects;
};

export const filterHandlingRequestsByTypeAndNoSplitStockLot = (
  handlingRequests: WarehouseHandlingRequest[],
  handlingRequestType: HandlingRequestType
): WarehouseHandlingRequest[] =>
  handlingRequests
    .filter((hr) => hr.requestType === handlingRequestType)
    .filter((hr) => !!hr.splitStockLot) as WarehouseHandlingRequest[];

export const isStockLotExpected = (
  stockLot: StockLotQuerySchemaLite | StockLotCRUDSchema
) =>
  stockLot.quant > 0 &&
  stockLot.stockLocation === null &&
  stockLot.facility !== null &&
  stockLot.status === 'processing' &&
  stockLot.pending === false &&
  'purchaseLineQuant' in stockLot &&
  stockLot.purchaseLineQuant === null &&
  stockLot.createdAt !== null &&
  stockLot.quarantined === false;

export const needsCutTapeCalculator = (
  packagingOption: PackagingOptions
): boolean => TAPE_PACKAGES.includes(packagingOption);

export const groupHandlingRequestsByStockLot = (
  requests: WarehouseHandlingRequest[]
): Record<string, WarehouseHandlingRequest[]> => {
  const groups: Record<string, WarehouseHandlingRequest[]> = {};

  requests.forEach((request) => {
    const stockLotId = request.stockLot.id;

    if (groups[stockLotId]) {
      groups[stockLotId].push(request);
    } else {
      groups[stockLotId] = [request];
    }
  });

  return groups;
};

const nextStationExecutionOrder = [
  HandlingRequestType.SPLIT,
  HandlingRequestType.SPLICE,
  HandlingRequestType.REEL,
  HandlingRequestType.REPACKAGE,
  HandlingRequestType.COMBINE,
  HandlingRequestType.CHECK_QUANT,
];

export const getNextHandlingRequest = (
  stockLotHandlingRequests: WarehouseHandlingRequest[],
  allHandlingRequests: ExpandedHandlingRequestWithGroupedStockLots[]
) => {
  if (stockLotHandlingRequests.length === 0) return null;
  const sortedRequests = [...stockLotHandlingRequests]
    .filter(
      (hr) =>
        hr.status === HandlingRequestStatus.REQUESTED &&
        nextStationExecutionOrder.includes(hr.requestType)
    )
    .sort(
      (a, b) =>
        nextStationExecutionOrder.indexOf(a.requestType) -
        nextStationExecutionOrder.indexOf(b.requestType)
    );
  return (
    allHandlingRequests.find((hr) =>
      hr.ids
        ? hr.ids.includes(sortedRequests[0]?.id || '')
        : hr.id === sortedRequests[0]?.id
    ) || null
  );
};
